import { type ConfigurableFilter, FILTER_TYPES } from '@openx/components-filters/lib/FiltersBar/types';
import type { ConstValues } from '@openx/types/helpers';
import type { Option } from '@openx/types/options';
import type { TFunction } from 'i18next';
import { type ExportTypeValue, exportType } from '../../../../../../../../modules/audiences/AudienceForm/constants';
import type { AudienceForm } from '../../../../../../../../modules/audiences/AudienceForm/types';
import type { ProviderMap } from '../../../hooks/useReferenceData';
export const AvailableFilters = {
  REACH: 'reach',
  PRICE: 'price',
  DATA_PARTNER: 'dataPartner',
  DATA_PROVIDER: 'dataProvider',
  DATA_TYPE: 'dataType',
  CATEGORY: 'category',
  SUB_CATEGORY: 'subCategory',
} as const;

export type AvailableFiltersKey = ConstValues<typeof AvailableFilters>;

export const mapObjectToOptions = (object: Record<string, string>): Option[] =>
  Object.entries(object)
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

const mapArrayToOptions = (array: string[]): Option[] =>
  array.map(item => ({
    id: item,
    name: item,
  }));

type FilterConfigProps = {
  t: TFunction;
  categories: string[];
  subCategories: string[];
  providers: ProviderMap;
  organizations: Record<string, string>;
  observableDirectProvider: AudienceForm['direct_audience_provider'];
  matchType: ExportTypeValue | null;
  isLookalike: boolean;
  hideDataTypeFilter: boolean;
  hideDataProviderFilter?: boolean;
  hideReachFilter?: boolean;
  hasOxpsSegments?: boolean;
};

const BidstreamFilters = [AvailableFilters.REACH, AvailableFilters.CATEGORY, AvailableFilters.SUB_CATEGORY];
const LookalikeFilters = [AvailableFilters.REACH, AvailableFilters.DATA_PROVIDER];

export const getFiltersConfig = ({
  t,
  categories,
  subCategories,
  providers,
  observableDirectProvider,
  matchType,
  isLookalike,
  hideDataTypeFilter,
  hideDataProviderFilter = false,
  hideReachFilter = false,
  organizations,
  hasOxpsSegments,
}: FilterConfigProps): ConfigurableFilter<AvailableFiltersKey>[] => {
  const allFilters: ConfigurableFilter<AvailableFiltersKey>[] = [
    {
      key: AvailableFilters.DATA_PARTNER,
      name: t('Data Partner'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: mapObjectToOptions(organizations),
      config: {
        selectedPlaceholder: t('[ Select Data Partner ]'),
        dropdownHeight: '220px',
      },
    },
    {
      key: AvailableFilters.DATA_TYPE,
      name: t('Data Type'),
      type: FILTER_TYPES.MULTI_OPTION_WITH_RADIO,
      options: [
        {
          id: 'past_browsing_interests',
          name: t('Web Browsing (Online Interests)'),
        },
        {
          id: 'shopping',
          name: t('Shopping'),
        },
        {
          id: 'location_visits',
          name: t('Location'),
        },
        ...(hasOxpsSegments
          ? [
              {
                id: 'purchase_propensity',
                name: t('Purchase Propensity'),
              },
            ]
          : []),
        {
          id: 'lifestyles',
          name: t('Lifestyles'),
        },
        {
          id: 'attitudes',
          name: t('Attitudes'),
        },
        {
          id: 'advanced_demographics',
          name: t('Advanced Demographics'),
        },
      ],
      config: {
        optionsWithRadio: ['advanced_demographics', 'lifestyles', 'shopping', 'purchase_propensity'],
        availableOptions: [
          { id: 'all', name: t('All') },
          { id: 'low', name: t('Low') },
          { id: 'medium', name: t('Medium') },
          { id: 'high', name: t('High') },
        ],
        removedAvailableOptions: [{ optionId: 'purchase_propensity', removedOptionId: 'low' }],
        radioOptionsLabel: t('Propensity'),
      },
    },
    {
      key: AvailableFilters.CATEGORY,
      name: t('Category'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: mapArrayToOptions(categories),
      config: {
        selectedPlaceholder: t('[ Select Category ]'),
        dropdownHeight: '220px',
      },
    },
    {
      key: AvailableFilters.REACH,
      name: t('Reach'),
      type: FILTER_TYPES.RANGE,
      config: {
        inputType: 'integer',
        precision: 0,
        detailedOptionsConfig: {
          minFieldPlaceholder: t('Enter Min. value'),
          maxFieldPlaceholder: t('Enter Max. value'),
        },
      },
    },
    {
      key: AvailableFilters.PRICE,
      name: t('Price'),
      type: FILTER_TYPES.MULTI_OPTION_WITH_RANGE,
      options: [
        {
          id: 'pricePom',
          name: t('Percent of Media'),
        },
        {
          id: 'priceCpm',
          name: t('Fixed CPM'),
        },
      ],
      config: {
        inputType: 'float',
        precision: 2,
        detailedOptionsConfig: {
          minFieldPlaceholder: t('Enter Min. value'),
          maxFieldPlaceholder: t('Enter Max. value'),
          multiOptionsConfig: [
            {
              endAdornment: '%',
            },
            {
              endAdornment: '$',
            },
          ],
        },
      },
    },
    {
      key: AvailableFilters.DATA_PROVIDER,
      name: t('Data Provider'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: mapObjectToOptions(providers),
      config: {
        selectedPlaceholder: t('[ Select Data Provider ]'),
        dropdownHeight: '220px',
      },
    },
    {
      key: AvailableFilters.SUB_CATEGORY,
      name: t('Sub Category'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: mapArrayToOptions(subCategories),
      config: {
        selectedPlaceholder: t('[ Select Sub Category ]'),
        dropdownHeight: '220px',
      },
    },
  ];

  if (observableDirectProvider && matchType !== exportType.OA_MATCH && !isLookalike) {
    return allFilters.filter(filter => BidstreamFilters.includes(filter.key as (typeof BidstreamFilters)[number]));
  }

  if (isLookalike) {
    return allFilters
      .filter(filter => LookalikeFilters.includes(filter.key as (typeof LookalikeFilters)[number]))
      .filter(filter => !(hideDataProviderFilter && filter.key === AvailableFilters.DATA_PROVIDER))
      .filter(filter => !(hideReachFilter && filter.key === AvailableFilters.REACH));
  }

  let configuredFilters = allFilters;

  if (hideDataTypeFilter) {
    configuredFilters = configuredFilters.filter(filter => filter.key !== AvailableFilters.DATA_TYPE);
  }

  if (hideDataProviderFilter) {
    configuredFilters = configuredFilters.filter(filter => filter.key !== AvailableFilters.DATA_PROVIDER);
  }

  if (hideReachFilter) {
    configuredFilters = configuredFilters.filter(filter => filter.key !== AvailableFilters.REACH);
  }

  return configuredFilters;
};
